import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza disposição/tempo × com quem vai × o que quer ver
 * e recomenda 1 a 3 trilhas. Eixos definidos a partir das dúvidas reais das
 * SERPs (radar de conteúdo): "qual a mais fácil", "dá com criança",
 * "quanto tempo leva", "o que tem pra ver". 100% client-side.
 */

type Answers = { tempo?: string; companhia?: string; motivo?: string };

// Perfil de cada trilha para o quiz
const TAGS: Record<
  string,
  { tempo: 'curto' | 'medio' | 'longo'; kids: boolean; motivo: string[]; nivel: number }
> = {
  'trilha-das-encantadas': { tempo: 'curto', kids: true, motivo: ['lenda'], nivel: 1 },
  'trilha-do-farol': { tempo: 'curto', kids: true, motivo: ['vista'], nivel: 2 },
  'trilha-praia-do-miguel': { tempo: 'medio', kids: false, motivo: ['mata', 'vista'], nivel: 2 },
  'trilha-da-fortaleza': { tempo: 'medio', kids: true, motivo: ['historia'], nivel: 2 },
  'trilha-encantadas-nova-brasilia': { tempo: 'medio', kids: false, motivo: ['mata'], nivel: 3 },
  'volta-completa-ilha': { tempo: 'longo', kids: false, motivo: ['mata', 'vista'], nivel: 4 },
};

const STEPS = [
  {
    key: 'tempo' as const,
    title: 'Quanto tempo (e fôlego) você tem?',
    options: [
      { v: 'curto', label: 'Uma caminhada curtinha (até ~1h)', icon: '⏱️' },
      { v: 'medio', label: 'Meio período, ritmo tranquilo (1–3h)', icon: '🚶' },
      { v: 'longo', label: 'Dia inteiro — topo desafio', icon: '💪' },
    ],
  },
  {
    key: 'companhia' as const,
    title: 'Com quem você vai?',
    options: [
      { v: 'kids', label: 'Com crianças pequenas ou idosos', icon: '🧒' },
      { v: 'ativos', label: 'Casal ou amigos ativos', icon: '👫' },
      { v: 'trilheiro', label: 'Trilheiro experiente / grupo forte', icon: '🥾' },
    ],
  },
  {
    key: 'motivo' as const,
    title: 'O que você quer encontrar no fim?',
    options: [
      { v: 'vista', label: 'A vista panorâmica do alto', icon: '📸' },
      { v: 'historia', label: 'História e patrimônio (canhões!)', icon: '🏰' },
      { v: 'lenda', label: 'A gruta e a lenda das sereias', icon: '🧜‍♀️' },
      { v: 'mata', label: 'Mata Atlântica e praias desertas', icon: '🌿' },
    ],
  },
];

const MOTIVO_LABEL: Record<string, string> = {
  vista: 'Vista panorâmica',
  historia: 'História',
  lenda: 'Gruta e lenda',
  mata: 'Imersão na mata',
};

function score(handle: string, a: Answers): number {
  const t = TAGS[handle];
  if (!t) return -Infinity;
  let s = 0;
  // motivo define O QUÊ (peso maior)
  if (a.motivo && t.motivo.includes(a.motivo)) s += 4;
  // tempo/fôlego filtra a viabilidade
  if (a.tempo === 'curto') s += t.tempo === 'curto' ? 3 : t.tempo === 'medio' ? 0 : -4;
  if (a.tempo === 'medio') s += t.tempo === 'medio' ? 3 : t.tempo === 'curto' ? 1.5 : -2;
  if (a.tempo === 'longo') s += t.tempo === 'longo' ? 3 : 1;
  // companhia
  if (a.companhia === 'kids') s += t.kids ? 3 : -4;
  if (a.companhia === 'trilheiro') s += t.nivel >= 3 ? 2 : 0;
  if (a.companhia === 'ativos') s += t.nivel === 2 || t.nivel === 3 ? 1.5 : 0;
  return s;
}

function reasons(handle: string, a: Answers): string[] {
  const t = TAGS[handle];
  const out: string[] = [];
  if (a.motivo && t?.motivo.includes(a.motivo)) out.push(MOTIVO_LABEL[a.motivo]);
  else if (t?.motivo[0]) out.push(MOTIVO_LABEL[t.motivo[0]]);
  if (a.tempo === 'curto' && t?.tempo === 'curto') out.push('Rapidinha');
  if (a.companhia === 'kids' && t?.kids) out.push('Vai bem com crianças');
  if (a.companhia === 'trilheiro' && (t?.nivel ?? 0) >= 3) out.push('Desafio de verdade');
  return [...new Set(out.filter(Boolean))].slice(0, 3);
}

export function TrilhasQuiz({ trilhas }: { trilhas: any[] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  let results: any[] = [];
  if (done) {
    const ranked = trilhas
      .filter(p => TAGS[p.handle])
      .map(p => ({ p, s: score(p.handle, answers) }))
      .sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    results = ranked.filter(r => r.s >= top - 1.5 && r.s > 0).slice(0, 3).map(r => r.p);
  }

  return (
    <section className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="quiz">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">Teste rápido</span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">Qual trilha combina com você?</h2>
        <p className="font-body text-sand-100/90 mb-8">Responda 3 perguntas e eu indico por onde caminhar.</p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`} />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">Passo {step + 1} de {STEPS.length}</p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">{STEPS[step].title}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                  <span className="font-body font-medium text-warm-dark">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark">
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores opções' : 'A trilha com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">
              {results.length ? `Encontrei ${results.length === 1 ? 'a trilha certa' : `${results.length} trilhas`} pra você` : 'Explore as trilhas abaixo'}
            </h3>
            <div className="space-y-4">
              {results.map((p, i) => (
                <div key={p.handle} className={`flex gap-4 p-3 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}>
                  <Link to={`/trilha/${p.handle}`} className="block w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={96} height={96} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    {i === 0 && <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">★ Melhor combinação</span>}
                    <h4 className="font-display text-lg text-warm-dark leading-tight">{p.title}</h4>
                    <div className="flex flex-wrap gap-1.5 my-1.5">
                      {reasons(p.handle, answers).map((r, j) => (
                        <span key={j} className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{r}</span>
                      ))}
                      <span className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{p.distance}</span>
                    </div>
                    <Link to={`/trilha/${p.handle}`} className="font-body text-sm font-semibold text-teal-deep hover:underline">
                      Ver o guia desta trilha →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={reset} className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark">↻ Refazer o teste</button>
          </div>
        )}
      </div>
    </section>
  );
}
